import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageShell, { PageHero, SectionTitle } from './PageShell';
import { enseignantPage } from '../mockPages';
import { ArrowRight, ArrowUpRight, UserCheck, Quote, ChevronRight } from 'lucide-react';
import FeaturedDemosSection from '../components/FeaturedDemosSection';
import { useAuth } from '../context/AuthContext';

export default function EnseignantPage() {
  const navigate = useNavigate();
  const { user, openRegister } = useAuth();
  const goTool = (to) => {
    if (to.startsWith('/enseignant/') && !user) { openRegister(); return; }
    navigate(to);
  };
  return (
    <PageShell>
      <PageHero data={enseignantPage.hero} />

      <FeaturedDemosSection variant="dashboard" />

      {/* Tools grid */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-12 sm:mt-20">
        <SectionTitle
          kicker="Tes outils ECSR"
          title="6 modules conçus pour la salle et la voiture."
          sub="Pensés par des moniteurs ECSR en exercice, validés sur le terrain, livrés prêts à l’emploi."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {enseignantPage.tools.map((t, i) => (
            <div key={i} className="group rounded-2xl border border-white/10 bg-[#0d0d0e] p-7 hover:border-red-700/40 transition">
              <div className="flex items-start justify-between mb-4">
                <span className="font-mono-spec text-red-500/80 text-[13px] group-hover:text-red-500">{t.code}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{t.meta}</span>
              </div>
              <h3 className="font-serif-editorial text-white text-[22px] leading-tight">{t.title}</h3>
              <p className="text-white/60 text-[13.5px] mt-3 leading-relaxed">{t.desc}</p>
              <button onClick={() => goTool(t.to)} data-testid={`tool-link-${t.code}`} className="mt-5 inline-flex items-center gap-1.5 text-red-400 text-[13px] font-semibold hover:text-red-300">Accéder <ChevronRight size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <SectionTitle kicker="Ils utilisent Code Voiture" title="La parole aux moniteurs ECSR." />
        <div className="grid md:grid-cols-3 gap-4">
          {enseignantPage.testimonials.map((t, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#141416] to-[#0c0c0d] p-7">
              <Quote className="text-red-500/70" size={22} />
              <p className="text-white/85 text-[15px] leading-relaxed mt-4 font-serif-editorial italic">«&nbsp;{t.quote}&nbsp;»</p>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t border-white/5">
                <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                <div>
                  <div className="text-white text-[14px] font-semibold">{t.name}</div>
                  <div className="text-white/50 text-[12px]">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a0a0a] to-[#0d0d0e] p-5 sm:p-8 lg:p-14 grid lg:grid-cols-[1.4fr_1fr] gap-6 sm:gap-10 items-end">
          <div>
            <div className="flex items-center gap-2 text-red-400 mb-3"><UserCheck size={18} /><span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold">Auto-école ou indépendant ?</span></div>
            <h3 className="font-serif-editorial text-white text-[24px] sm:text-[32px] lg:text-[44px] leading-[1.05] sm:leading-[1.02]">Connecte ton équipe à Code Voiture.</h3>
            <p className="text-white/65 text-[13.5px] sm:text-[14.5px] mt-3 sm:mt-4 max-w-xl">Profil enseignant ECSR vérifié, tableau de bord par élève, exports REMC, formation des moniteurs juniors. Activation immédiate.</p>
            <div className="mt-5 sm:mt-6 flex gap-2 sm:gap-3 flex-wrap">
              <Link to="/demande-info?type=ecsr_verification" data-testid="ensei-cta-verify-ecsr" className="h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40">Vérifier mon profil enseignant <ArrowRight size={15} /></Link>
              <Link to="/demande-info?type=demo" data-testid="ensei-cta-demo" className="h-11 sm:h-12 px-5 sm:px-6 rounded-full border border-white/15 text-white text-[13px] sm:text-[14px] font-medium hover:bg-white/5 inline-flex items-center">Demander une démo</Link>
            </div>
          </div>
          <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0a0a0b] p-4 sm:p-6 lg:p-7">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/50 font-bold mb-3 sm:mb-4">Inclus avec ton compte ECSR</div>
            <ul className="space-y-2 sm:space-y-3">
              {[
                'Tableau de bord multi-élèves',
                'Exports REMC (PDF / CSV)',
                'Synchronisation livret 38 compétences',
                'Banque magazine éditoriale (31 enquêtes + 9 dossiers ETG)',
                'Accès prioritaire au support',
              ].map((s, i) => (
                <li key={i} className="flex items-center gap-2 text-white/80 text-[12.5px] sm:text-[14px]"><ChevronRight size={14} className="text-red-400 shrink-0" /> {s}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
